import React from "react";
import { Box, Container, Typography, Button, Grid } from "@mui/material";
import QRCode from "react-qr-code";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.ayuja.app&hl=en_IN";
const APP_STORE_URL = "https://apps.apple.com/in/app/ayuja/id6758427368";

// Inline SVG badges (no external image dependency)
function PlayStoreBadge({ url }) {
  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: "#000",
        borderRadius: "10px",
        px: 2,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        textTransform: "none",
        color: "#fff",
        minWidth: 160,
        "&:hover": { backgroundColor: "#222" },
      }}
    >
      {/* Google Play icon */}
      <svg width="28" height="28" viewBox="0 0 512 512" fill="none">
        <path d="M70 7.7L291 256 70 504.3A28 28 0 0156 480V32a28 28 0 0114-24.3z" fill="#EA4335"/>
        <path d="M386 196l-57-33-78 93 78 93 58-33c22-13 22-107-1-120z" fill="#FBBC04"/>
        <path d="M70 7.7A28 28 0 0196 4l239 139-78 93L70 7.7z" fill="#4285F4"/>
        <path d="M70 504.3l187-228 78 93L96 508a28 28 0 01-26-3.7z" fill="#34A853"/>
      </svg>
      <Box textAlign="left">
        <Typography sx={{ fontSize: "10px", color: "#ccc", lineHeight: 1 }}>GET IT ON</Typography>
        <Typography sx={{ fontSize: "16px", fontWeight: "bold", lineHeight: 1.3 }}>Google Play</Typography>
      </Box>
    </Button>
  );
}

function AppStoreBadge({ url }) {
  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: "#000",
        borderRadius: "10px",
        px: 2,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        textTransform: "none",
        color: "#fff",
        minWidth: 160,
        "&:hover": { backgroundColor: "#222" },
      }}
    >
      {/* Apple icon */}
      <svg width="26" height="26" viewBox="0 0 814 1000" fill="white">
        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.4-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 134.4-317.5 266.5-317.5 99.8 0 183.2 65.8 245.8 65.8 60.4 0 156.2-69.2 270.1-69.2zM549.8 149.1c-5.6-36.5-1.4-72.9 11.4-104.7 34.3-12.7 75.3.1 101.2 27.6 26.2 27.8 40.5 64.5 39.8 101.4-36.5 5.9-75.6-6-107.8-24.3z"/>
      </svg>
      <Box textAlign="left">
        <Typography sx={{ fontSize: "10px", color: "#ccc", lineHeight: 1 }}>Available on the</Typography>
        <Typography sx={{ fontSize: "16px", fontWeight: "bold", lineHeight: 1.3 }}>App Store</Typography>
      </Box>
    </Button>
  );
}

function AppDownloadSection() {
  return (
    <Box
      sx={{
        // backgroundColor: "#eaf9f5",
        py: { xs: 5, md: 6 },
        px: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          spacing={{ xs: 4, md: 6 }}
        >
          {/* QR Codes */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 3, sm: 4 },
              }}
            >
              {/* Play Store QR */}
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: 3,
                    p: 2,
                    boxShadow: "0 4px 20px rgba(8,124,124,0.15)",
                    border: "2px solid #22577A",
                    display: "inline-block",
                  }}
                >
                  <QRCode
                    value={PLAY_STORE_URL}
                    size={130}
                    fgColor="#22577A"
                    bgColor="#fff"
                  />
                </Box>
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: "13px",
                    fontFamily: '"Montserrat", sans-serif',
                    fontWeight: 600,
                    color: "#22577A",
                  }}
                >
                  Scan for Android
                </Typography>
              </Box>

              {/* App Store QR */}
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: 3,
                    p: 2,
                    boxShadow: "0 4px 20px rgba(8,124,124,0.15)",
                    border: "2px solid #087c7c",
                    display: "inline-block",
                  }}
                >
                  <QRCode
                    value={APP_STORE_URL}
                    size={130}
                    fgColor="#087c7c"
                    bgColor="#fff"
                  />
                </Box>
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: "13px",
                    fontFamily: '"Montserrat", sans-serif',
                    fontWeight: 600,
                    color: "#087c7c",
                  }}
                >
                  Scan for iOS
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Divider */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              width: "1px",
              height: 180,
              backgroundColor: "#b2dfd9",
              mx: 2,
            }}
          />

          {/* Text + Badges */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                textAlign: { xs: "center", md: "left" },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 800,
                  fontSize: { xs: "24px", sm: "28px", md: "34px" },
                  background: "linear-gradient(to bottom, #22577A, seagreen)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                  mb: 1.5,
                }}
              >
                Download Our App Now
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontSize: { xs: "14px", md: "16px" },
                  color: "#444",
                  mb: 3,
                  lineHeight: 1.6,
                  maxWidth: 340,
                  mx: { xs: "auto", md: 0 },
                }}
              >
                Book care services, track your caregiver, and manage appointments — all from your phone, anytime, anywhere.
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#087c7c",
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Available on:
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: { xs: "center", md: "flex-start" },
                  alignItems: "center",
                }}
              >
                <PlayStoreBadge url={PLAY_STORE_URL} />
                <AppStoreBadge url={APP_STORE_URL} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default AppDownloadSection;